import {match} from 'ts-pattern';

import {selectHorizontalSeparatorsVisible} from '../../store/createEagGridStore';
import {useGridSettingsSafe} from '../../store/selectors/useGridSettingsSafe';
import {useEagGridSelector} from '../../store/useEagGridSelector';
import type {EagColumn} from '../../types/EagColumn';
import type {EagColumnState} from '../../types/EagColumnState';
import type {RowData} from '../../types/RowData';
import {LineHeightToPx} from '../../utils/lineHeightToValue';
import {GridRow} from './components/GridRow/GridRow';
import {SkeletonRow} from './SkeletonRow';

export type GridBodyProps = {
  columns: EagColumn[];
  columnStates: EagColumnState[];
  rows: RowData[];
  previousRows: RowData[];
  skeletonRowCount: number;
  isLoading: boolean;
  selectedRowIds: Set<string>;
  onRowClick?: (row: RowData, event: React.MouseEvent) => void;
  pinnedLeftOffsets: Map<string, number>;
  pinnedRightOffsets: Map<string, number>;
  stripedRows?: boolean;
  lastPinnedLeftKey: string | null;
  firstPinnedRightKey: string | null;
  // Action column props — only present when action column is enabled
  onActionClick?: (actionKey: string, row: RowData, event: React.MouseEvent) => void;
  actionColumnWidth?: number;
  isMobile?: boolean;
  disableAllActions?: boolean;
  // Selection props — controls whether rows render a RowSelectCheckbox
  rowSelectMode: 'SINGLE' | 'MULTIPLE' | 'NONE';
  onToggleSelection?: (rowId: string) => void;
};

export function GridBody(props: GridBodyProps) {
  const skeletonIndices = Array.from({length: props.skeletonRowCount}, (_, i) => i);
  const gridSettings = useGridSettingsSafe();
  const skeletonHeightPx = LineHeightToPx[gridSettings.rowHeight];
  const horizontalSeparators = useEagGridSelector(selectHorizontalSeparatorsVisible);

  return match({
    isLoading: props.isLoading,
    isEmpty: props.rows.length === 0 && !props.isLoading,
  })
    .with({isLoading: true}, () => (
      <tbody>
        <tr role="status" className="sr-only" aria-label="Loading" />
        {skeletonIndices.map((index) => (
          <SkeletonRow
            key={index}
            heightPx={skeletonHeightPx}
            columnStates={props.columnStates}
            horizontalSeparators={horizontalSeparators}
            actionColumnWidth={props.actionColumnWidth}
          />
        ))}
      </tbody>
    ))
    .with({isEmpty: true}, () => <tbody />)
    .otherwise(() => (
      <tbody>
        {props.rows.map((row, index) => (
          <GridRow
            key={row.id}
            columns={props.columns}
            columnStates={props.columnStates}
            row={row}
            isSelected={props.selectedRowIds.has(row.id)}
            onRowClick={props.onRowClick}
            pinnedLeftOffsets={props.pinnedLeftOffsets}
            pinnedRightOffsets={props.pinnedRightOffsets}
            isStriped={props.stripedRows && index % 2 === 1}
            rowIndex={index}
            lastPinnedLeftKey={props.lastPinnedLeftKey}
            firstPinnedRightKey={props.firstPinnedRightKey}
            onActionClick={props.onActionClick}
            actionColumnWidth={props.actionColumnWidth}
            isMobile={props.isMobile}
            disableAllActions={props.disableAllActions}
            rowSelectMode={props.rowSelectMode}
            onToggleSelection={props.onToggleSelection}
          />
        ))}
      </tbody>
    ));
}
